import { useState } from "react";
import { useStore } from "../store.ts";
import { logAttempt } from "../api.ts";

/**
 * Results.
 *
 * Two jobs. Tell the player how they did, and — because this build exists to
 * answer whether the number feels right — ask them how they THINK they did.
 * The self-rating is written into the session log next to the score, which is
 * the only way to actually measure the correlation rather than guess at it.
 */
export function Results() {
  const store = useStore();
  const { result, round, config } = store;
  const [rating, setRating] = useState<number | null>(null);
  const [saved, setSaved] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  if (!result || !round) return null;

  const rushed = result.summary.rushedPct;
  const early = result.summary.earlyMs;

  async function submitRating(value: number) {
    setRating(value);
    const attempt = useStore.getState().lastAttempt;
    if (!attempt) return;
    // Re-log the same attempt id with the rating attached. The log is
    // append-only, and the retune script keeps the last line per id — so this
    // reads as an update without needing a mutable store.
    try {
      await logAttempt({ ...attempt, selfRating: value as 1 | 2 | 3 | 4 | 5 });
      setSaved(true);
    } catch {
      setSaved(false);
    }
  }

  return (
    <>
      <div className="panel">
        <div className="stage" style={{ minHeight: 180 }}>
          {result.rejected ? (
            <>
              <div className="state-label">Not scored</div>
              <div className="big">—</div>
              <p className="note bad" style={{ maxWidth: 460 }}>
                {result.rejected.detail}
              </p>
            </>
          ) : (
            <>
              <div className="state-label">Score</div>
              <div className="huge">{Math.round(result.score)}</div>
              <div className="sub">
                {Math.round(result.baseScore)} base &times;{" "}
                {result.tierMultiplier.toFixed(1)} ({result.tier})
              </div>
            </>
          )}
        </div>

        {!result.rejected && (
          <>
            <div className="stats">
              <div className="stat">
                <div className="k">Tempo</div>
                <div className="v">
                  {Math.abs(rushed) < 0.5
                    ? "dead on"
                    : `${rushed > 0 ? "rushed" : "dragged"} ${Math.abs(rushed).toFixed(1)}%`}
                </div>
              </div>
              <div className="stat">
                <div className="k">Entry</div>
                <div className="v">
                  {Math.abs(early) < 15
                    ? "on the beat"
                    : `${Math.abs(early).toFixed(0)}ms ${early < 0 ? "early" : "late"}`}
                </div>
              </div>
              <div className="stat">
                <div className="k">Tightness</div>
                <div className="v">{(result.residualMedianAbs * 1000).toFixed(0)}ms</div>
              </div>
              <div className="stat">
                <div className="k">Best streak</div>
                <div className="v">{result.bestStreak}</div>
              </div>
              <div className="stat">
                <div className="k">Hit / miss / extra</div>
                <div className="v" style={{ fontSize: 16 }}>
                  {result.summary.hitCount} / {result.summary.missCount} /{" "}
                  {result.summary.extraCount}
                </div>
              </div>
              <div className="stat">
                <div className="k">Coverage</div>
                <div className="v">{(result.coverage * 100).toFixed(0)}%</div>
              </div>
            </div>

            <p className="note">
              Tempo and entry are <em>not</em> penalties — being uniformly 8% fast
              means you held a clock, just not the song's. You're scored on how far
              each word strayed from your own line, which is why those two numbers
              sit beside the score rather than inside it.
              {result.tempoFitFellBack &&
                " (Your tempo was outside the plausible range, so this attempt was scored against the song's own clock.)"}
            </p>

            <ResidualPlot result={result} config={config} />
          </>
        )}
      </div>

      <div className="panel">
        <h2>How did that feel?</h2>
        <p className="sub">
          This is the actual experiment: does the number above match your gut? Your
          answer is logged next to the score so the correlation can be measured
          instead of assumed.
        </p>
        <div className="rating">
          {[1, 2, 3, 4, 5].map((v) => (
            <button
              key={v}
              className={rating === v ? "on" : ""}
              onClick={() => void submitRating(v)}
            >
              {v}
            </button>
          ))}
        </div>
        <p className="sub" style={{ marginTop: 8 }}>
          1 = all over the place · 5 = nailed it {saved && "· saved"}
        </p>
      </div>

      <div className="row">
        <button
          className="primary"
          onClick={() => useStore.getState().set({ screen: "play", result: null })}
        >
          Try again
        </button>
        <button onClick={() => useStore.getState().set({ screen: "pick-round" })}>
          Another section
        </button>
        <button onClick={() => setShowDetail((s) => !s)}>
          {showDetail ? "Hide" : "Show"} per-word detail
        </button>
      </div>

      {showDetail && !result.rejected && (
        <div className="panel">
          <h2>Per-onset residuals</h2>
          <p className="sub">
            Residual is deviation from your own fitted line. Raw is deviation from
            the recording.
          </p>
          <table className="pairs">
            <thead>
              <tr>
                <th>#</th>
                <th>Expected</th>
                <th>You sang</th>
                <th>Residual</th>
                <th>Raw</th>
                <th>Streak</th>
                <th>Points</th>
              </tr>
            </thead>
            <tbody>
              {result.pairs.map((p, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{p.expected.toFixed(2)}s</td>
                  <td>{p.detected.toFixed(2)}s</td>
                  <td
                    style={{
                      color:
                        Math.abs(p.residual) < config.streak.threshold
                          ? "var(--accent)"
                          : Math.abs(p.residual) > config.residual.cutoff
                            ? "var(--bad)"
                            : "var(--warn)",
                    }}
                  >
                    {(p.residual * 1000).toFixed(0)}ms
                  </td>
                  <td style={{ color: "var(--muted)" }}>
                    {(p.rawDeviation * 1000).toFixed(0)}ms
                  </td>
                  <td>{p.streakAtThisPoint}</td>
                  <td>{p.points.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/** Residual over time. Flat and centred = held the clock; fanning out = drifted. */
function ResidualPlot({
  result,
  config,
}: {
  result: NonNullable<ReturnType<typeof useStore.getState>["result"]>;
  config: ReturnType<typeof useStore.getState>["config"];
}) {
  if (result.pairs.length < 2) return null;

  const W = 700;
  const H = 90;
  const pad = 6;
  const xs = result.pairs.map((p) => p.expected);
  const x0 = Math.min(...xs);
  const x1 = Math.max(...xs);
  const yMax = Math.max(config.residual.cutoff, ...result.pairs.map((p) => Math.abs(p.residual)));

  const px = (x: number) => pad + ((x - x0) / Math.max(1e-6, x1 - x0)) * (W - 2 * pad);
  const py = (y: number) => H / 2 - (y / yMax) * (H / 2 - pad);

  return (
    <svg className="viz" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img"
         aria-label="Residual deviation over the course of the section">
      <line x1={0} y1={H / 2} x2={W} y2={H / 2} stroke="var(--line)" strokeWidth={1} />
      <rect
        x={0}
        y={py(config.streak.threshold)}
        width={W}
        height={py(-config.streak.threshold) - py(config.streak.threshold)}
        fill="var(--accent)"
        opacity={0.07}
      />
      {result.pairs.map((p, i) => (
        <circle
          key={i}
          cx={px(p.expected)}
          cy={py(p.residual)}
          r={2.5}
          fill={
            Math.abs(p.residual) < config.streak.threshold
              ? "var(--accent)"
              : Math.abs(p.residual) > config.residual.cutoff
                ? "var(--bad)"
                : "var(--warn)"
          }
        />
      ))}
    </svg>
  );
}
